import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { downloadFile } from '@utils/downloadFile';
import removeEmptyParams from 'src/utils/formatters/removeEmptyParams';
import api from '@api';
import {
    IGetCreativesDataFormatted,
    IGetOffersDataFormatted,
    IGetSubsDataFormatted,
    IGetTransactionsSummaryStatDataFormatted,
    IResponse,
} from '@api/types';
import {
    ITransactionsCard,
    ITransactionsFilterOption,
    ITransactionsFilterValues,
    ITransactionsParamsFromFilter,
} from '@pages/statistics/transactions/interfaces';
import { CurrencyTypes } from 'src/types';
import {
    defaultCurrencies,
    currencyPriority,
    rightPositionCurrencySigns,
    currenciesSigns,
    EXCEL_VALUE,
    cardsObj,
} from './config';

export const formatOffersForSelect = (
    array: IGetOffersDataFormatted[],
): ITransactionsFilterOption<number>[] =>
    array.map(({ id, name }) => ({
        value: id,
        label: name,
    }));

export const formatCreativesForSelect = (
    array: IGetCreativesDataFormatted[],
): ITransactionsFilterOption<string>[] =>
    array.map(({ id, name }) => ({
        value: id,
        label: name,
    }));

/**
 * Достает первый ключ со значением из объекта сабов и форматирует для селекта
 */
export const prepareSubsSelectOptions = (
    subs: IGetSubsDataFormatted[],
): ITransactionsFilterOption<string>[] =>
    subs.reduce(
        (
            subsOptions: { label: string; value: string }[],
            { id: value, subs: subsObj }: IGetSubsDataFormatted,
        ) => {
            const subKeyWithValue: string = Object.keys(subsObj).find(key => subsObj[key]);
            const label: string = subsObj[subKeyWithValue];
            const option = { label, value };

            subsOptions.push(option);
            return subsOptions;
        },
        [],
    );

/**
 * Достает общие параметры для методов транзакции и summary
 */
export const getSharedParamsFromFilter = (
    filters: ITransactionsFilterValues,
): ITransactionsParamsFromFilter => {
    const { creativeId: creative, subId: sub, clickId, currency, tsFrom, tsTo, fields } = filters;

    return {
        tsFrom: tsFrom && dayjs(tsFrom).format('YYYY-MM-DD'),
        tsTo: tsTo && dayjs(tsTo).format('YYYY-MM-DD'),
        ...(creative && { creative }),
        ...(sub && { sub: Number(sub) }),
        ...(clickId && { clickId }),
        ...(currency && { currency }),
        ...(fields && { fields }),
    };
};

export const addCurrencySign = (val: number, currency: string): string =>
    rightPositionCurrencySigns.includes(currency)
        ? `${val} ${currenciesSigns[currency]}`
        : `${currenciesSigns[currency]} ${val}`;

// todo type
export const prepareCards = (
    cards: typeof cardsObj,
    summary: IGetTransactionsSummaryStatDataFormatted,
    currency: string,
): ITransactionsCard[] =>
    Object.keys(cards).map((status: string) => {
        const { count = 0, commission = 0 } = summary?.[status] || {};

        return {
            ...cards[status],
            count,
            commission: addCurrencySign(commission, currency),
        };
    });

/**
 * Достает валюты из оффера
 * форматирует их и создает массив
 * Массив отсортировывается на основе приоритетов
 */
export const prepareCurrencies = (
    offerId: number | string = null,
    offers: IGetOffersDataFormatted[] = [],
): ITransactionsFilterOption<string>[] => {
    if (!Array.isArray(offers)) {
        return defaultCurrencies;
    }

    const offerCurrencies: Set<CurrencyTypes> = new Set();
    const relevantOffers: IGetOffersDataFormatted[] =
        offerId === null
            ? offers
            : offers.filter(
                  (offer: IGetOffersDataFormatted): boolean => Number(offer.id) === Number(offerId),
              );

    relevantOffers.forEach((offer: IGetOffersDataFormatted): void => {
        if (Array.isArray(offer.currency)) {
            offer.currency.forEach((currency: CurrencyTypes): void => {
                offerCurrencies.add(currency);
            });
        }
    });

    // todo: это можно убрать с обновлением TS, пока без суббреса можно повлиять на все проверки итераторов
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const offerCurrencyOptions = [...offerCurrencies].map(currency => ({
        label: currency,
        value: currency,
    }));

    offerCurrencyOptions.sort(
        ({ value: fCurrency }, { value: sCurrency }) =>
            currencyPriority[fCurrency] - currencyPriority[sCurrency],
    );

    return offerCurrencyOptions.length ? offerCurrencyOptions : defaultCurrencies;
};

/**
 * Скачивает выгрузку транзакции
 * (зашит тип выгрузки Excel)
 * @param {Object} filters
 */
export const exportTransactions = async (filters: ITransactionsFilterValues): Promise<void> => {
    const {
        tsFrom,
        tsTo,
        subId: sub,
        offerId: offerIds,
        creativeId: creative,
        clickId,
        currency,
    } = filters;

    const pure_params = {
        fileFormat: EXCEL_VALUE,
        tsFrom: dayjs(tsFrom).format('YYYY-MM-DD'),
        tsTo: dayjs(tsTo).format('YYYY-MM-DD'),
        offerIds,
        currency,
        clickId,
        sub,
        creative,
        typeIds: '1,2,3',
    };

    const params: Record<string, unknown> = removeEmptyParams(pure_params);

    const exportTransactionsReq: void = await api.transactions
        .exportTransactions({ params })
        .then((res: IResponse<Blob> & { blob?: () => Promise<Blob> }): void => {
            const current_date = format(new Date(), 'yyyy-MM-dd');

            res.blob().then(blob => {
                downloadFile(`transactions${current_date}.xls`, blob);
            });
        });

    return exportTransactionsReq;
};

/**
 * Скачивает выгрузку транзакции (зашит тип выгрузки CSV)
 */
export const asyncTransaction = async (filters: ITransactionsFilterValues): Promise<unknown> => {
    const {
        tsFrom,
        tsTo,
        subId: sub,
        offerId: offerIds,
        creativeId: creative,
        clickId,
        currency,
    } = filters;

    const pure_params = {
        fileFormat: 'csv',
        tsFrom: dayjs(tsFrom).format('YYYY-MM-DD'),
        tsTo: dayjs(tsTo).format('YYYY-MM-DD'),
        offerIds,
        currency,
        clickId,
        sub,
        creative,
        typeIds: '1,2,3',
    };

    const params = removeEmptyParams(pure_params);
    const exportTransactionsReq = await api.transactions.exportAsyncTransactions({ params });

    return exportTransactionsReq;
};

/**
 * Достает первую валюту из поля currency у оффера
 * */
export const getDefaultCurrency = (
    availableCurrencies: ITransactionsFilterOption<string>[],
    currencyFromQueryParams: string,
    defaultCurrencyByLocale: string,
): string => {
    const currencyByParams: ITransactionsFilterOption<string> = availableCurrencies.find(
        curByParam => curByParam.label === currencyFromQueryParams,
    );
    const currencyByLocale: ITransactionsFilterOption<string> = availableCurrencies.find(
        curByLocale => curByLocale.label === defaultCurrencyByLocale,
    );

    // Есть ли выбранная локаль в queryParams
    if (currencyByParams !== undefined) {
        return currencyFromQueryParams;
    }

    if (currencyByLocale !== undefined) {
        return defaultCurrencyByLocale;
    }

    return availableCurrencies[0].label;
};

/**
 * @deprecated
 */
// todo: будем избавляться при рефакторинге
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useLocalStorage = (key, initialValue) => {
    const [storedValue, setStoredValue] = useState(initialValue);

    useEffect(() => {
        try {
            const hasWindow = typeof window !== 'undefined';
            const item = hasWindow && window.localStorage.getItem(key);
            setStoredValue(item ? JSON.parse(item) : initialValue);
        } catch (err) {
            Sentry.captureException(err);
            // eslint-disable-next-line no-console
            console.log(err);
            setStoredValue(initialValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setValue = value => {
        try {
            const valueToStore = value instanceof Function ? value(storedValue) : value;
            setStoredValue(valueToStore);
            window.localStorage.setItem(key, JSON.stringify(valueToStore));
        } catch (err) {
            Sentry.captureException(err);
            // eslint-disable-next-line no-console
            console.log(err);
        }
    };

    return [storedValue, setValue];
};
